import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, Wallet, TrendingUp, RefreshCw, Edit, Trash2, PieChart, Percent, DollarSign, AlertTriangle, CheckCircle2, ArrowRight, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ownersApi, profitAllocationsApi, projectsApi, ledgerApi, notesApi } from '../lib/api';
import { formatCurrency, formatDate, useDismissedBanner } from '../lib/utils';
import { toast } from 'sonner';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#1A2F23', '#C68E5D', '#3B82F6', '#8B5CF6', '#06B6D4'];

export const Ownership = () => {
  const [owners, setOwners] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOwnerForm, setShowOwnerForm] = useState(false);
  const [showAllocationForm, setShowAllocationForm] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [projectProfitInfo, setProjectProfitInfo] = useState(null);
  const [commissionStatus, setCommissionStatus] = useState(null);
  const [notePaymentStatus, setNotePaymentStatus] = useState(null);
  
  // Input mode: 'dollar' or 'percent'
  const [distributionMode, setDistributionMode] = useState('percent');
  const [reinvestmentMode, setReinvestmentMode] = useState('percent');

  const [ownerFormData, setOwnerFormData] = useState({
    name: '',
    ownership_percentage: ''
  });

  const [allocationFormData, setAllocationFormData] = useState({
    project_id: '',
    total_land_finder_profit: '',
    distribution_amount: '',
    distribution_percent: '50',
    reinvestment_amount: '',
    reinvestment_percent: '50',
    allocation_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ownersRes, allocationsRes, projectsRes, summaryRes] = await Promise.all([
        ownersApi.getAll(),
        profitAllocationsApi.getAll(),
        projectsApi.getAll(),
        ownersApi.getSummary()
      ]);
      setOwners(ownersRes.data);
      setAllocations(allocationsRes.data);
      setProjects(projectsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load ownership data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOwner = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: ownerFormData.name,
        ownership_percentage: parseFloat(ownerFormData.ownership_percentage) / 100,
        is_active: true
      };

      if (editingOwner) {
        await ownersApi.update(editingOwner.id, payload);
        toast.success('Owner updated');
      } else {
        await ownersApi.create(payload);
        toast.success('Owner added');
      }

      setShowOwnerForm(false);
      setEditingOwner(null);
      setOwnerFormData({ name: '', ownership_percentage: '' });
      loadData();
    } catch (error) {
      console.error('Failed to save owner:', error);
      toast.error('Failed to save owner');
    }
  };

  const handleSubmitAllocation = async (e) => {
    e.preventDefault();
    
    const profit = parseFloat(allocationFormData.total_land_finder_profit) || 0;
    
    // Calculate final dollar amounts based on mode
    let distributionAmount, reinvestmentAmount;
    
    if (distributionMode === 'percent') {
      distributionAmount = profit * (parseFloat(allocationFormData.distribution_percent) / 100);
    } else {
      distributionAmount = parseFloat(allocationFormData.distribution_amount) || 0;
    }
    
    if (reinvestmentMode === 'percent') {
      reinvestmentAmount = profit * (parseFloat(allocationFormData.reinvestment_percent) / 100);
    } else {
      reinvestmentAmount = parseFloat(allocationFormData.reinvestment_amount) || 0;
    }
    
    try {
      const payload = {
        project_id: allocationFormData.project_id,
        total_land_finder_profit: profit,
        distribution_amount: distributionAmount,
        reinvestment_amount: reinvestmentAmount,
        allocation_date: allocationFormData.allocation_date,
        notes: allocationFormData.notes || null
      };

      if (editingAllocation) {
        await profitAllocationsApi.update(editingAllocation.id, payload);
        toast.success('Allocation updated');
      } else {
        await profitAllocationsApi.create(payload);
        toast.success('Profit allocation recorded');
      }

      setShowAllocationForm(false);
      setEditingAllocation(null);
      setProjectProfitInfo(null);
      resetAllocationForm();
      loadData();
    } catch (error) {
      console.error('Failed to save allocation:', error);
      toast.error('Failed to save allocation');
    }
  };

  const resetAllocationForm = () => {
    setAllocationFormData({
      project_id: '',
      total_land_finder_profit: '',
      distribution_amount: '',
      distribution_percent: '50',
      reinvestment_amount: '',
      reinvestment_percent: '50',
      allocation_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setDistributionMode('percent');
    setReinvestmentMode('percent');
    setProjectProfitInfo(null);
    setCommissionStatus(null);
  };

  const handleEditOwner = (owner) => {
    setEditingOwner(owner);
    setOwnerFormData({
      name: owner.name,
      ownership_percentage: (owner.ownership_percentage * 100).toString()
    });
    setShowOwnerForm(true);
  };

  const handleEditAllocation = (allocation) => {
    setEditingAllocation(allocation);
    const profit = allocation.total_land_finder_profit || 0;
    const distPercent = profit > 0 ? ((allocation.distribution_amount / profit) * 100).toFixed(1) : '50';
    const reinvestPercent = profit > 0 ? ((allocation.reinvestment_amount / profit) * 100).toFixed(1) : '50';
    
    setAllocationFormData({
      project_id: allocation.project_id,
      total_land_finder_profit: allocation.total_land_finder_profit.toString(),
      distribution_amount: allocation.distribution_amount.toString(),
      distribution_percent: distPercent,
      reinvestment_amount: allocation.reinvestment_amount.toString(),
      reinvestment_percent: reinvestPercent,
      allocation_date: allocation.allocation_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      notes: allocation.notes || ''
    });
    setDistributionMode('dollar');
    setReinvestmentMode('dollar');
    setShowAllocationForm(true);
  };

  const handleDeleteOwner = async (ownerId) => {
    try {
      await ownersApi.delete(ownerId);
      toast.success('Owner deactivated');
      loadData();
    } catch (error) {
      toast.error('Failed to delete owner');
    }
  };

  const handleDeleteAllocation = async (allocationId) => {
    try {
      await profitAllocationsApi.delete(allocationId);
      toast.success('Allocation deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete allocation');
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  const handleProjectSelect = async (projectId) => {
    setAllocationFormData({ ...allocationFormData, project_id: projectId });
    setProjectProfitInfo(null);
    setCommissionStatus(null);
    setNotePaymentStatus(null);
    
    if (projectId) {
      try {
        // Fetch both profit info and commission status in parallel
        const [profitResult, commissionResult] = await Promise.all([
          projectsApi.getProfitInfo(projectId),
          ledgerApi.getProjectCommissionStatus(projectId)
        ]);
        
        setProjectProfitInfo(profitResult.data);
        setCommissionStatus(commissionResult.data);
        
        // For Note_Held projects, check if note payments have been received
        const selectedProject = projects.find(p => p.id === projectId);
        if (selectedProject?.status === 'Note_Held') {
          try {
            const notesResult = await notesApi.getAll(projectId);
            const projectNotes = notesResult.data || [];
            const totalReceived = projectNotes.reduce((sum, n) => sum + (n.total_received || 0), 0);
            const hasPayments = totalReceived > 0;
            setNotePaymentStatus({
              hasPayments,
              totalReceived,
              noteCount: projectNotes.length
            });
          } catch {
            setNotePaymentStatus({ hasPayments: false, totalReceived: 0, noteCount: 0 });
          }
        }
        
        // Auto-populate with NET profit (after commissions)
        const netProfit = profitResult.data.land_finder_net_profit ?? profitResult.data.land_finder_profit;
        if (netProfit > 0) {
          setAllocationFormData(prev => ({
            ...prev,
            project_id: projectId,
            total_land_finder_profit: netProfit.toString(),
            distribution_amount: (netProfit * 0.5).toFixed(2),
            reinvestment_amount: (netProfit * 0.5).toFixed(2)
          }));
        }
      } catch (error) {
        console.error('Failed to get project info:', error);
      }
    }
  };

  // Handle profit change and update amounts based on percentages
  const handleProfitChange = (value) => {
    const profit = parseFloat(value) || 0;
    const distPct = parseFloat(allocationFormData.distribution_percent) || 0;
    const reinvPct = parseFloat(allocationFormData.reinvestment_percent) || 0;
    
    setAllocationFormData({
      ...allocationFormData,
      total_land_finder_profit: value,
      distribution_amount: (profit * (distPct / 100)).toFixed(2),
      reinvestment_amount: (profit * (reinvPct / 100)).toFixed(2)
    });
  };

  // Handle distribution percent change
  const handleDistributionPercentChange = (value) => {
    const profit = parseFloat(allocationFormData.total_land_finder_profit) || 0;
    const pct = parseFloat(value) || 0;
    const remaining = 100 - pct;
    
    setAllocationFormData({
      ...allocationFormData,
      distribution_percent: value,
      distribution_amount: (profit * (pct / 100)).toFixed(2),
      reinvestment_percent: remaining.toString(),
      reinvestment_amount: (profit * (remaining / 100)).toFixed(2)
    });
  };

  // Handle reinvestment percent change
  const handleReinvestmentPercentChange = (value) => {
    const profit = parseFloat(allocationFormData.total_land_finder_profit) || 0;
    const pct = parseFloat(value) || 0;
    const remaining = 100 - pct;
    
    setAllocationFormData({
      ...allocationFormData,
      reinvestment_percent: value,
      reinvestment_amount: (profit * (pct / 100)).toFixed(2),
      distribution_percent: remaining.toString(),
      distribution_amount: (profit * (remaining / 100)).toFixed(2)
    });
  };

  // Handle distribution dollar change
  const handleDistributionDollarChange = (value) => {
    const profit = parseFloat(allocationFormData.total_land_finder_profit) || 0;
    const amount = parseFloat(value) || 0;
    const remaining = profit - amount;
    
    setAllocationFormData({
      ...allocationFormData,
      distribution_amount: value,
      distribution_percent: profit > 0 ? ((amount / profit) * 100).toFixed(1) : '0',
      reinvestment_amount: remaining.toFixed(2),
      reinvestment_percent: profit > 0 ? ((remaining / profit) * 100).toFixed(1) : '0'
    });
  };

  // Handle reinvestment dollar change
  const handleReinvestmentDollarChange = (value) => {
    const profit = parseFloat(allocationFormData.total_land_finder_profit) || 0;
    const amount = parseFloat(value) || 0;
    const remaining = profit - amount;
    
    setAllocationFormData({
      ...allocationFormData,
      reinvestment_amount: value,
      reinvestment_percent: profit > 0 ? ((amount / profit) * 100).toFixed(1) : '0',
      distribution_amount: remaining.toFixed(2),
      distribution_percent: profit > 0 ? ((remaining / profit) * 100).toFixed(1) : '0'
    });
  };

  // Pie chart data
  const distributionData = [
    { name: 'Distributed', value: summary?.total_distributed || 0 },
    { name: 'Reinvested', value: summary?.total_reinvested || 0 }
  ].filter(d => d.value > 0);

  const ownerDistributionData = summary?.owner_distributions?.map((od, index) => ({
    name: od.owner_name,
    value: od.total_distribution
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="ownership-loading">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ownership-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Ownership & Profit Splits</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Track Land Finder profit distribution and reinvestment</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={() => {
              setEditingOwner(null);
              setOwnerFormData({ name: '', ownership_percentage: '' });
              setShowOwnerForm(true);
            }}
            className="rounded-sm"
            data-testid="add-owner-btn"
          >
            <Users className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Add Owner</span>
          </Button>
          <Button 
            onClick={() => {
              setEditingAllocation(null);
              resetAllocationForm();
              setShowAllocationForm(true);
            }}
            className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold"
            data-testid="add-allocation-btn"
          >
            <Plus className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Record Profit</span><span className="sm:hidden">Profit</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card className="rounded-sm border" data-testid="total-profit-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Total Land Finder Profit</p>
                <p className="text-2xl font-heading font-bold mt-1 text-forest-500">
                  {formatCurrency(summary?.total_land_finder_profit || 0)}
                </p>
              </div>
              <TrendingUp className="w-5 h-5 text-forest-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="distributed-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Total Distributed</p>
                <p className="text-2xl font-heading font-bold mt-1 text-emerald-600">
                  {formatCurrency(summary?.total_distributed || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{summary?.distribution_percentage || 0}% of profit</p>
              </div>
              <Wallet className="w-5 h-5 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="reinvested-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Total Reinvested</p>
                <p className="text-2xl font-heading font-bold mt-1 text-blue-600">
                  {formatCurrency(summary?.total_reinvested || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{summary?.reinvestment_percentage || 0}% of profit</p>
              </div>
              <RefreshCw className="w-5 h-5 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="allocations-count-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Profit Allocations</p>
                <p className="text-2xl font-heading font-bold mt-1">{summary?.allocations_count || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">recorded deals</p>
              </div>
              <PieChart className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Owners & Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Owners List */}
        <Card className="rounded-sm border" data-testid="owners-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide">Ownership Structure</CardTitle>
            <CardDescription>Company ownership percentages</CardDescription>
          </CardHeader>
          <CardContent>
            {owners.length > 0 ? (
              <div className="space-y-3">
                {owners.map((owner, index) => (
                  <div key={owner.id} className="flex items-center justify-between p-3 border rounded-sm">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-sm" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <div>
                        <p className="font-medium">{owner.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(owner.ownership_percentage * 100).toFixed(1)}% ownership
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-mono font-semibold text-emerald-600">
                          {formatCurrency((summary?.total_distributed || 0) * owner.ownership_percentage)}
                        </p>
                        <p className="text-xs text-muted-foreground">total distributed</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditOwner(owner)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-sm">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Deactivate Owner</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to deactivate {owner.name}?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDeleteOwner(owner.id)}
                                className="bg-red-600 hover:bg-red-700 rounded-sm"
                              >
                                Deactivate
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Total check */}
                <div className="pt-2 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Ownership</span>
                    <span className={`font-semibold ${
                      owners.reduce((sum, o) => sum + o.ownership_percentage, 0) === 1 
                        ? 'text-emerald-600' 
                        : 'text-amber-600'
                    }`}>
                      {(owners.reduce((sum, o) => sum + o.ownership_percentage, 0) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No owners configured yet.</p>
                <Button 
                  variant="link" 
                  onClick={() => setShowOwnerForm(true)}
                  className="mt-2"
                >
                  Add your first owner
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Distribution Chart */}
        <Card className="rounded-sm border" data-testid="distribution-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide">Profit Allocation</CardTitle>
            <CardDescription>Distribution vs Reinvestment</CardDescription>
          </CardHeader>
          <CardContent>
            {distributionData.length > 0 ? (
              <div>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={distributionData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="value"
                        label={false}
                      >
                        <Cell fill="#22C55E" />
                        <Cell fill="#3B82F6" />
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-2">
                  {distributionData.map((item, i) => (
                    <div key={item.name} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: i === 0 ? '#22C55E' : '#3B82F6' }} />
                      <span className="text-muted-foreground">{item.name}</span>
                      <span className="font-mono font-semibold">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No profit allocations recorded yet.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Allocations History */}
      <Card className="rounded-sm border" data-testid="allocations-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide">Profit Allocation History</CardTitle>
        </CardHeader>
        <CardContent>
          {allocations.length > 0 ? (
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="data-table min-w-[600px]">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Project</th>
                    <th>Land Finder Profit</th>
                    <th>Distributed</th>
                    <th>Reinvested</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((allocation) => (
                    <tr key={allocation.id}>
                      <td className="font-mono text-sm">{formatDate(allocation.allocation_date)}</td>
                      <td>
                        <Link 
                          to={`/projects/${allocation.project_id}`}
                          className="text-primary hover:underline"
                        >
                          {getProjectName(allocation.project_id)}
                        </Link>
                      </td>
                      <td className="font-mono font-medium">
                        {formatCurrency(allocation.total_land_finder_profit)}
                      </td>
                      <td className="font-mono text-emerald-600">
                        {formatCurrency(allocation.distribution_amount)}
                      </td>
                      <td className="font-mono text-blue-600">
                        {formatCurrency(allocation.reinvestment_amount)}
                      </td>
                      <td className="text-muted-foreground max-w-[150px] truncate">
                        {allocation.notes || '-'}
                      </td>
                      <td>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditAllocation(allocation)}
                            className="h-7 w-7 p-0"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="rounded-sm">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Allocation</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this profit allocation?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteAllocation(allocation.id)}
                                  className="bg-red-600 hover:bg-red-700 rounded-sm"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No profit allocations recorded yet. Record your first profit split to start tracking.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Owner Dialog */}
      <Dialog open={showOwnerForm} onOpenChange={(open) => {
        setShowOwnerForm(open);
        if (!open) setEditingOwner(null);
      }}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingOwner ? 'Edit Owner' : 'Add Owner'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitOwner} className="space-y-4">
            <div className="space-y-2">
              <Label>Owner Name *</Label>
              <Input
                value={ownerFormData.name}
                onChange={(e) => setOwnerFormData({ ...ownerFormData, name: e.target.value })}
                required
                className="rounded-sm"
                placeholder="e.g., Joe Smith"
              />
            </div>
            <div className="space-y-2">
              <Label>Ownership Percentage (%) *</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={ownerFormData.ownership_percentage}
                onChange={(e) => setOwnerFormData({ ...ownerFormData, ownership_percentage: e.target.value })}
                required
                className="rounded-sm"
                placeholder="e.g., 33.33"
              />
              <p className="text-xs text-muted-foreground">Enter as percentage (e.g., 33.33 for 33.33%)</p>
            </div>
            <Button 
              type="submit" 
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold"
            >
              {editingOwner ? 'Update Owner' : 'Add Owner'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Allocation Dialog */}
      <Dialog open={showAllocationForm} onOpenChange={(open) => {
        setShowAllocationForm(open);
        if (!open) {
          setEditingAllocation(null);
          setProjectProfitInfo(null);
        }
      }}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingAllocation ? 'Edit Profit Allocation' : 'Record Profit Allocation'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitAllocation} className="space-y-4">
            <div className="space-y-2">
              <Label>Project *</Label>
              <Select
                value={allocationFormData.project_id}
                onValueChange={handleProjectSelect}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.filter(p => p.status === 'Sold' || p.status === 'Note_Held').map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                      {project.status === 'Note_Held' && <span className="text-orange-600 ml-1">(Note Held)</span>}
                    </SelectItem>
                  ))}
                  {projects.filter(p => p.status !== 'Sold' && p.status !== 'Note_Held').length > 0 && (
                    <>
                      <SelectItem value="divider" disabled>── Other Projects ──</SelectItem>
                      {projects.filter(p => p.status !== 'Sold' && p.status !== 'Note_Held').map(project => (
                        <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {projectProfitInfo && (
              <div className="p-3 bg-white rounded-sm border text-sm">
                <p className="font-medium text-blue-800">Project Profit Info</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div>Sale Price: {formatCurrency(projectProfitInfo.sale_price)}</div>
                  <div>Gross Profit: {formatCurrency(projectProfitInfo.gross_profit)}</div>
                  {projectProfitInfo.has_jv && (
                    <div>Investor Profit: {formatCurrency(projectProfitInfo.investor_profit)}</div>
                  )}
                  <div>
                    Land Finder Profit: {formatCurrency(projectProfitInfo.land_finder_profit)}
                  </div>
                  {projectProfitInfo.total_commissions > 0 && (
                    <div className="text-red-600">
                      - Commissions: {formatCurrency(projectProfitInfo.total_commissions)}
                    </div>
                  )}
                  <div className="font-semibold text-emerald-700 col-span-2 pt-1 border-t border-blue-200">
                    Net to Owners: {formatCurrency(projectProfitInfo.land_finder_net_profit ?? projectProfitInfo.land_finder_profit)}
                  </div>
                </div>
              </div>
            )}

            {/* Commission Status Warning */}
            {commissionStatus && !commissionStatus.can_record_profit && (
              <div className="p-3 bg-amber-50 rounded-sm border border-amber-300 text-sm" data-testid="commission-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Commission Required</p>
                    <p className="text-xs text-amber-700 mt-1">
                      {commissionStatus.message}
                    </p>
                    {commissionStatus.inherited_from_parent && (
                      <p className="text-xs text-purple-600 mt-1 font-medium">
                        Commission eligibility inherited from parent: {commissionStatus.parent_project_name}
                      </p>
                    )}
                    {commissionStatus.employees_missing_commission?.length > 0 && (
                      <div className="mt-2 text-xs text-amber-700">
                        <span className="font-medium">Missing commission for:</span>
                        <ul className="list-disc ml-4 mt-1">
                          {commissionStatus.employees_missing_commission.map((emp, idx) => (
                            <li key={idx}>{emp.name} ({(emp.commission_tier_1_rate * 100).toFixed(0)}% T1 rate)</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <Link 
                      to="/ledger"
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-800 hover:text-amber-900 underline"
                    >
                      Go to Employee Ledger to Record Commission <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* No Employees Assigned - Info */}
            {commissionStatus && commissionStatus.eligible_employees_count === 0 && commissionStatus.can_record_profit && (
              <div className="p-3 bg-white rounded-sm border text-sm" data-testid="no-commission-employees">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">No Commission Required</p>
                    <p className="text-xs text-blue-700 mt-1">
                      No employees are assigned for commission on this project{commissionStatus.is_child_lot ? ' or its parent' : ''}. Profit can be allocated directly.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Commission Recorded Success */}
            {commissionStatus && commissionStatus.commission_recorded && commissionStatus.can_record_profit && commissionStatus.eligible_employees_count > 0 && (
              <div className="p-3 bg-white rounded-sm border text-sm" data-testid="commission-recorded">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-emerald-800">Commission Recorded</p>
                    {commissionStatus.inherited_from_parent && (
                      <p className="text-xs text-purple-600 mt-1">
                        Inherited from parent: {commissionStatus.parent_project_name}
                      </p>
                    )}
                    <p className="text-xs text-emerald-700 mt-1">
                      Total commission: {formatCurrency(commissionStatus.total_commission)} 
                      ({commissionStatus.commission_entries.length} entry/entries)
                    </p>
                    {commissionStatus.commission_entries.map((entry, idx) => (
                      <p key={idx} className="text-xs text-emerald-600 mt-0.5">
                        • {entry.employee_name}: {formatCurrency(entry.amount)}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Note Held - Payments Required Warning */}
            {notePaymentStatus && !notePaymentStatus.hasPayments && (
              <div className="p-3 bg-orange-50 rounded-sm border border-orange-300 text-sm" data-testid="note-payments-required">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-orange-800">Note Payments Required</p>
                    <p className="text-xs text-orange-700 mt-1">
                      This is a Note Held deal. Owner profit cannot be recorded until cash has been received. 
                      Record note payments in Notes Receivable first.
                    </p>
                    <Link 
                      to="/notes"
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-orange-800 hover:text-orange-900 underline"
                    >
                      Go to Notes Receivable <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {notePaymentStatus && notePaymentStatus.hasPayments && (
              <div className="p-3 bg-white rounded-sm border text-sm" data-testid="note-payments-received">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-emerald-800">Note Payments Received</p>
                    <p className="text-xs text-emerald-700 mt-1">
                      Total received: {formatCurrency(notePaymentStatus.totalReceived)}. Profit can be recorded.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Total Land Finder Profit ($) *</Label>
              <Input
                type="text"
                value={allocationFormData.total_land_finder_profit ? Number(allocationFormData.total_land_finder_profit).toLocaleString('en-US') : ''}
                onChange={(e) => handleProfitChange(e.target.value.replace(/[^0-9.]/g, ''))}
                required
                className="rounded-sm"
                placeholder="Auto-populated from project or enter manually"
              />
            </div>

            {/* Distribution Input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Distribution Amount</Label>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant={distributionMode === 'dollar' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDistributionMode('dollar')}
                    className="h-7 px-2 rounded-sm"
                  >
                    <DollarSign className="w-3 h-3" />
                  </Button>
                  <Button
                    type="button"
                    variant={distributionMode === 'percent' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDistributionMode('percent')}
                    className="h-7 px-2 rounded-sm"
                  >
                    <Percent className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              {distributionMode === 'percent' ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={allocationFormData.distribution_percent}
                    onChange={(e) => handleDistributionPercentChange(e.target.value)}
                    className="rounded-sm"
                    placeholder="50"
                  />
                  <span className="text-muted-foreground">%</span>
                  <span className="text-sm text-muted-foreground ml-2">
                    = {formatCurrency(parseFloat(allocationFormData.distribution_amount) || 0)}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">$</span>
                  <Input
                    type="text"
                    value={allocationFormData.distribution_amount ? Number(allocationFormData.distribution_amount).toLocaleString('en-US') : ''}
                    onChange={(e) => handleDistributionDollarChange(e.target.value.replace(/[^0-9.]/g, ''))}
                    className="rounded-sm"
                    placeholder="0"
                  />
                  <span className="text-sm text-muted-foreground ml-2">
                    = {allocationFormData.distribution_percent}%
                  </span>
                </div>
              )}
              <p className="text-xs text-emerald-600">Amount to distribute to owners</p>
            </div>

            {/* Reinvestment Input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Reinvestment Amount</Label>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant={reinvestmentMode === 'dollar' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setReinvestmentMode('dollar')}
                    className="h-7 px-2 rounded-sm"
                  >
                    <DollarSign className="w-3 h-3" />
                  </Button>
                  <Button
                    type="button"
                    variant={reinvestmentMode === 'percent' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setReinvestmentMode('percent')}
                    className="h-7 px-2 rounded-sm"
                  >
                    <Percent className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              {reinvestmentMode === 'percent' ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={allocationFormData.reinvestment_percent}
                    onChange={(e) => handleReinvestmentPercentChange(e.target.value)}
                    className="rounded-sm"
                    placeholder="50"
                  />
                  <span className="text-muted-foreground">%</span>
                  <span className="text-sm text-muted-foreground ml-2">
                    = {formatCurrency(parseFloat(allocationFormData.reinvestment_amount) || 0)}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">$</span>
                  <Input
                    type="text"
                    value={allocationFormData.reinvestment_amount ? Number(allocationFormData.reinvestment_amount).toLocaleString('en-US') : ''}
                    onChange={(e) => handleReinvestmentDollarChange(e.target.value.replace(/[^0-9.]/g, ''))}
                    className="rounded-sm"
                    placeholder="0"
                  />
                  <span className="text-sm text-muted-foreground ml-2">
                    = {allocationFormData.reinvestment_percent}%
                  </span>
                </div>
              )}
              <p className="text-xs text-blue-600">Amount to reinvest in business</p>
            </div>

            <div className="space-y-2">
              <Label>Allocation Date</Label>
              <Input
                type="date"
                value={allocationFormData.allocation_date}
                onChange={(e) => setAllocationFormData({ ...allocationFormData, allocation_date: e.target.value })}
                className="rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={allocationFormData.notes}
                onChange={(e) => setAllocationFormData({ ...allocationFormData, notes: e.target.value })}
                className="rounded-sm"
                placeholder="Optional notes about this allocation..."
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={(commissionStatus && !commissionStatus.can_record_profit) || (notePaymentStatus && !notePaymentStatus.hasPayments)}
              data-testid="submit-allocation-btn"
            >
              {editingAllocation ? 'Update Allocation' : 'Record Allocation'}
            </Button>
            {commissionStatus && !commissionStatus.can_record_profit && (
              <p className="text-xs text-center text-amber-600 mt-2">
                Record commission first to enable profit allocation
              </p>
            )}
            {notePaymentStatus && !notePaymentStatus.hasPayments && (
              <p className="text-xs text-center text-orange-600 mt-2">
                Note payments must be received before profit can be allocated
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
